import { loadSettings, saveSettings } from '../../onboarding/settings.js';
import { ModelSelectorComponent } from '../components/model-selector.js';
import type { ModelItem } from '../components/model-selector.js';
import { askModalQuestion } from '../modal-question.js';
import { showModalOverlay } from '../overlay.js';
import { promptForApiKeyIfNeeded } from '../prompt-api-key.js';
import type { SlashCommandContext } from './types.js';

const BUILT_IN_SUBAGENT_TYPES: Array<{ id: string; label: string; description: string }> = [
  {
    id: 'explore',
    label: 'Explore',
    description: 'Read-only codebase exploration',
  },
  {
    id: 'plan',
    label: 'Plan',
    description: 'Read-only analysis and planning',
  },
  {
    id: 'execute',
    label: 'Execute',
    description: 'Task execution with write access',
  },
];

async function showSubagentModelListForScope(
  ctx: SlashCommandContext,
  scope: 'global' | 'thread',
  agentType: string,
  agentTypeLabel: string,
): Promise<void> {
  const availableModels = await ctx.state.harness.listAvailableModels();

  if (availableModels.length === 0) {
    ctx.showInfo('No models available. Check your Mastra configuration.');
    return;
  }

  const currentSubagentModel = ctx.state.harness.getSubagentModelId({ agentType });
  const scopeLabel = scope === 'global' ? `${agentTypeLabel} · Global` : `${agentTypeLabel} · Thread`;

  return new Promise(resolve => {
    const selector = new ModelSelectorComponent({
      tui: ctx.state.ui,
      models: availableModels,
      currentModelId: currentSubagentModel ?? undefined,
      title: `Select subagent model (${scopeLabel})`,
      onSelect: async (model: ModelItem) => {
        ctx.state.ui.hideOverlay();
        await promptForApiKeyIfNeeded(ctx.state.ui, model, ctx.authStorage);
        try {
          await ctx.state.harness.setSubagentModelId({ modelId: model.id, agentType });
          if (scope === 'global') {
            const settings = loadSettings();
            settings.models.subagentModels[agentType] = model.id;
            saveSettings(settings);
          }
          ctx.showInfo(`Subagent model set for ${scopeLabel}: ${model.id}`);
        } catch (err) {
          ctx.showError(`Failed to set subagent model: ${err instanceof Error ? err.message : String(err)}`);
        }
        resolve();
      },
      onCancel: () => {
        ctx.state.ui.hideOverlay();
        resolve();
      },
    });

    showModalOverlay(ctx.state.ui, selector, { widthPercent: 0.8, maxHeight: '60%' });
    selector.focused = true;
  });
}

async function showSubagentScopeThenList(
  ctx: SlashCommandContext,
  agentType: string,
  agentTypeLabel: string,
): Promise<void> {
  const scopes = [
    {
      label: 'Thread default',
      description: `Default for ${agentTypeLabel} subagents in this thread`,
      scope: 'thread' as const,
    },
    {
      label: 'Global default',
      description: `Default for ${agentTypeLabel} subagents in all threads`,
      scope: 'global' as const,
    },
  ];

  const answer = await askModalQuestion(ctx.state.ui, {
    question: `Select scope for ${agentTypeLabel} subagents`,
    options: scopes.map(s => ({
      label: s.label,
      description: s.description,
    })),
  });

  try {
    const selected = scopes.find(s => s.label === answer);
    if (selected) {
      await showSubagentModelListForScope(ctx, selected.scope, agentType, agentTypeLabel);
    }
  } catch (err) {
    ctx.showError(`Subagent selection failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}

function getConfiguredSubagentTypes(
  ctx: SlashCommandContext,
): Array<{ id: string; label: string; description: string }> {
  const harnessWithConfig = ctx.state.harness as unknown as {
    config?: {
      subagents?: Array<{ id: string; name: string; description: string }>;
    };
  };
  const configuredSubagents = harnessWithConfig.config?.subagents;

  return configuredSubagents && configuredSubagents.length > 0
    ? configuredSubagents.map(subagent => ({
        id: subagent.id,
        label: subagent.name,
        description: subagent.description,
      }))
    : BUILT_IN_SUBAGENT_TYPES;
}

export async function handleSubagentsCommand(ctx: SlashCommandContext): Promise<void> {
  const agentTypes = getConfiguredSubagentTypes(ctx);

  const answer = await askModalQuestion(ctx.state.ui, {
    question: 'Select subagent type',
    options: agentTypes.map(t => ({
      label: t.label,
      description: t.description,
    })),
  });

  try {
    const selected = agentTypes.find(t => t.label === answer);
    if (selected) {
      await showSubagentScopeThenList(ctx, selected.id, selected.label);
    }
  } catch (err) {
    ctx.showError(`Subagent selection failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}
